import React, { useEffect, useMemo, useState } from "react";
import { Barbell, DownloadSimple, Calendar, Timer, ChartLineUp, CheckCircle, X } from "@phosphor-icons/react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { generateTrainingsSummaryPDF } from "../../lib/trainingsSummaryPdf";

const MES_LARGO = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];

function formatMonth(ym) {
  if (!ym || ym.length < 7) return ym || "";
  const [y, m] = ym.split("-");
  const idx = parseInt(m, 10) - 1;
  if (idx < 0 || idx > 11) return ym;
  return `${MES_LARGO[idx]} ${y}`;
}

function formatDuration(sec) {
  if (!sec || sec <= 0) return "—";
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function formatDistance(m) {
  if (!m || m <= 0) return "—";
  return m >= 1000 ? `${(m / 1000).toFixed(1)} km` : `${Math.round(m)} m`;
}

function formatVolume(kg) {
  if (!kg || kg <= 0) return "—";
  return `${Math.round(kg).toLocaleString("es-AR")} kg`;
}

export default function TrainingsSummaryTab({ userId, targetUser, isAdmin }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [yearFilter, setYearFilter] = useState("all");

  useEffect(() => {
    setLoading(true);
    api.get(`/users/${userId}/trainings/summary`)
      .then(({ data }) => setData(data))
      .catch((err) => toast.error(formatApiError(err)))
      .finally(() => setLoading(false));
  }, [userId]);

  const years = useMemo(() => {
    if (!data?.by_month) return [];
    const ySet = new Set(data.by_month.map((m) => m.month.slice(0, 4)));
    return ["all", ...Array.from(ySet).sort().reverse()];
  }, [data]);

  const filteredMonths = useMemo(() => {
    if (!data?.by_month) return [];
    if (yearFilter === "all") return data.by_month;
    return data.by_month.filter((m) => m.month.startsWith(yearFilter));
  }, [data, yearFilter]);

  const handleDownload = async () => {
    if (!data) return;
    setDownloading(true);
    try {
      await generateTrainingsSummaryPDF(data, { yearFilter });
      toast.success("PDF generado");
    } catch (err) {
      console.error(err);
      toast.error("No se pudo generar el PDF");
    } finally {
      setDownloading(false);
    }
  };

  if (loading) return <div className="text-muted py-12 text-center">Cargando resumen...</div>;
  if (!data) return <div className="text-muted py-12 text-center">Sin datos disponibles.</div>;

  const t = data.totals || {};

  return (
    <div className="space-y-6" data-testid="trainings-summary-tab">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-2">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted">Historial de entrenamientos</div>
          <h2 className="font-display font-black uppercase text-2xl tracking-tighter">Resumen mensual</h2>
          {isAdmin && (
            <div className="text-xs text-muted mt-1">
              Atleta: <span className="text-white font-semibold">{data.user?.name}</span>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          {years.length > 1 && (
            <select
              className="input-base !w-auto !py-2 text-sm"
              value={yearFilter}
              onChange={(e) => setYearFilter(e.target.value)}
              data-testid="trainings-year-filter"
            >
              {years.map((y) => (
                <option key={y} value={y}>{y === "all" ? "Todos los años" : y}</option>
              ))}
            </select>
          )}
          <button
            onClick={handleDownload}
            disabled={downloading || !data}
            className="btn-primary !py-2 !px-4 text-sm flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="download-trainings-pdf"
          >
            <DownloadSimple size={16} weight="bold" />
            {downloading ? "Generando..." : "Descargar PDF"}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="trainings-totals">
        <div className="bg-[#0A0A0A] border border-app p-4">
          <div className="text-[10px] uppercase tracking-widest text-muted flex items-center gap-1">
            <Barbell size={10} weight="bold" /> Sesiones
          </div>
          <div className="font-display font-black text-2xl text-primary-app mt-1">{t.sessions_count || 0}</div>
          <div className="text-[10px] text-muted mt-1">
            {t.completed_count || 0} completas · {t.aborted_count || 0} abortadas
          </div>
        </div>
        <div className="bg-[#0A0A0A] border border-app p-4">
          <div className="text-[10px] uppercase tracking-widest text-muted">Volumen Fuerza</div>
          <div className="font-display font-black text-2xl mt-1">{formatVolume(t.total_volume_kg)}</div>
          <div className="text-[10px] text-muted mt-1">kg levantados</div>
        </div>
        <div className="bg-[#0A0A0A] border border-app p-4">
          <div className="text-[10px] uppercase tracking-widest text-muted">Vol Aeróbico</div>
          <div className="font-display font-black text-2xl mt-1" style={{ color: t.total_distance_m ? "#32D74B" : undefined }}>
            {formatDistance(t.total_distance_m)}
          </div>
        </div>
        <div className="bg-[#0A0A0A] border border-app p-4">
          <div className="text-[10px] uppercase tracking-widest text-muted flex items-center gap-1">
            <ChartLineUp size={10} weight="bold" /> RPE prom
          </div>
          <div className="font-display font-black text-2xl mt-1" style={{ color: (t.avg_rpe || 0) >= 8 ? "#FF9500" : undefined }}>
            {t.avg_rpe != null ? t.avg_rpe.toFixed(1) : "—"}
          </div>
        </div>
        <div className="bg-[#0A0A0A] border border-app p-4">
          <div className="text-[10px] uppercase tracking-widest text-muted flex items-center gap-1">
            <Timer size={10} weight="bold" /> Tiempo total
          </div>
          <div className="font-display font-black text-2xl mt-1">{formatDuration(t.total_duration_sec)}</div>
        </div>
      </div>

      <div>
        <div className="text-xs uppercase tracking-widest text-muted mb-3 flex items-center gap-2">
          <Calendar size={12} weight="bold" /> Detalle por mes
        </div>
        {filteredMonths.length === 0 ? (
          <div className="text-muted text-sm border border-app p-4 text-center">
            Sin entrenamientos {yearFilter !== "all" ? `en ${yearFilter}` : "registrados"} todavía.
          </div>
        ) : (
          <div className="space-y-3" data-testid="trainings-months-list">
            {filteredMonths.map((m) => (
              <div key={m.month} className="bg-[#0A0A0A] border border-app p-4" data-testid={`trainings-month-${m.month}`}>
                <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                  <div className="font-display font-black uppercase tracking-tight">{formatMonth(m.month)}</div>
                  <div className="flex items-center gap-3 text-xs">
                    <span className="text-[10px] uppercase tracking-widest text-muted">{m.sessions_count} sesiones</span>
                    <span className="text-primary-app font-semibold">{formatVolume(m.total_volume_kg)}</span>
                    {m.total_distance_m > 0 && (
                      <span style={{ color: "#32D74B" }} className="font-semibold">{formatDistance(m.total_distance_m)}</span>
                    )}
                    {m.avg_rpe != null && (
                      <span className="text-muted">RPE {m.avg_rpe.toFixed(1)}</span>
                    )}
                  </div>
                </div>
                <div className="divide-y divide-[#1a1a1a]">
                  {m.sessions.map((s) => (
                    <div key={s.session_id} className="py-2 flex items-center justify-between text-sm gap-2">
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        {s.status === "completed" ? (
                          <CheckCircle size={14} weight="fill" className="text-[#32D74B] flex-shrink-0" />
                        ) : s.status === "aborted" ? (
                          <X size={14} weight="bold" className="text-[#FF9500] flex-shrink-0" />
                        ) : (
                          <Timer size={14} weight="bold" className="text-muted flex-shrink-0" />
                        )}
                        <div className="min-w-0">
                          <div className="font-semibold truncate">{s.routine_name || "Sesión libre"}</div>
                          <div className="text-[11px] text-muted">
                            {s.started_at?.slice(0, 10)} · {s.sets_count || 0} series
                            {s.duration_sec > 0 ? ` · ${formatDuration(s.duration_sec)}` : ""}
                          </div>
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <div className="font-display font-bold">{formatVolume(s.volume_kg)}</div>
                        <div className="text-[11px] text-muted">
                          {s.distance_m > 0 ? formatDistance(s.distance_m) : ""}
                          {s.distance_m > 0 && s.avg_rpe ? " · " : ""}
                          {s.avg_rpe != null ? `RPE ${s.avg_rpe}` : ""}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="text-[10px] uppercase tracking-widest text-muted text-center pt-4">
        Generado: {new Date(data.generated_at).toLocaleString("es-AR")}
      </div>
    </div>
  );
}
